import { useState } from "react";
import { Download, Share, SquarePlus, CheckCircle2, Compass, Copy, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useI18n } from "@/i18n/I18nProvider";
import { useInstallPrompt, detectPlatform } from "@/lib/pwa";

const Step = ({ n, icon: Icon, text }) => (
  <li className="flex items-start gap-3">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#000B45] text-sm font-bold text-[#FEB601]">{n}</span>
    <span className="flex flex-1 items-center gap-2 pt-1 text-sm text-[#111827] sm:text-base">
      {text} {Icon && <Icon className="h-5 w-5 shrink-0 text-[#000B45]" />}
    </span>
  </li>
);

const STEP_SETS = {
  iosSafari: [["install.iosStep1", Compass], ["install.iosStep2", Share], ["install.iosStep3", SquarePlus], ["install.iosStep4", CheckCircle2]],
  iosOther: [["install.otherStep1", Copy], ["install.otherStep2", Compass], ["install.iosStep2", Share], ["install.iosStep3", SquarePlus]],
  generic: [["install.genericStep1", MoreVertical], ["install.genericStep2", Download], ["install.iosStep4", CheckCircle2]],
};

const InstallInstructions = ({ open, onOpenChange, variant }) => {
  const { t } = useI18n();
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      toast.success(t("install.linkCopied"));
    } catch {
      toast.error(t("errors.generic"));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="install-instructions-dialog" closeLabel={t("common.close")} className="w-[calc(100vw-2rem)] max-w-md rounded-xl">
        <DialogHeader className="items-center text-center">
          <img src="/brand/icon-v2-192.png" alt="" className="mb-2 h-16 w-16" />
          <DialogTitle className="font-display text-xl text-[#000B45]">{t("install.title")}</DialogTitle>
          {variant === "iosOther" && <DialogDescription>{t("install.otherIntro")}</DialogDescription>}
        </DialogHeader>
        <ol className="mt-2 space-y-4" data-testid={`install-steps-${variant}`}>
          {STEP_SETS[variant].map(([key, icon], i) => <Step key={key} n={i + 1} icon={icon} text={t(key)} />)}
        </ol>
        {variant === "iosOther" && (
          <button type="button" onClick={copyLink} data-testid="install-copy-link"
            className="mt-2 flex min-h-[48px] items-center justify-center gap-2 rounded-lg border border-[#000B45] text-sm font-bold text-[#000B45] transition-colors hover:bg-[#000B45] hover:text-white">
            <Copy className="h-4 w-4" /> {t("install.copyLink")}
          </button>
        )}
        <button type="button" onClick={() => onOpenChange(false)} data-testid="install-instructions-close"
          className="mt-2 min-h-[48px] rounded-lg bg-[#000B45] text-sm font-bold text-white transition-colors hover:bg-[#000624]">
          {t("common.close")}
        </button>
      </DialogContent>
    </Dialog>
  );
};

// Real install only: native prompt where supported, otherwise manual instructions. Hidden inside the installed app.
export const InstallAppButton = ({ testId = "install-app-button", className = "" }) => {
  const { t } = useI18n();
  const { canPrompt, installed, promptInstall } = useInstallPrompt();
  const [variant, setVariant] = useState(null);
  if (installed) return null;

  const onClick = async () => {
    if (canPrompt) return promptInstall();
    const { ios, iosSafari } = detectPlatform();
    setVariant(ios ? (iosSafari ? "iosSafari" : "iosOther") : "generic");
  };

  return (
    <>
      <button type="button" onClick={onClick} data-testid={testId}
        className={`flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg border-2 border-[#000B45] bg-white px-5 text-sm font-bold text-[#000B45] transition-colors hover:bg-[#000B45] hover:text-white ${className}`}>
        <Download className="h-4 w-4" /> {t("install.button")}
      </button>
      {variant && <InstallInstructions open onOpenChange={() => setVariant(null)} variant={variant} />}
    </>
  );
};
